import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// ============= COLORS =============
export const COLORS = {
  primary: '#2563eb',
  secondary: '#7c3aed',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  chart: ['#2563eb', '#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#ec4899']
};

// ============= FEMA CORE CAPABILITIES BY MISSION AREA =============
export const MISSION_AREAS = {
  prevention: {
    name: 'Prevention',
    color: 'blue',
    capabilities: [
      'Planning',
      'Public Information and Warning',
      'Operational Coordination',
      'Intelligence and Information Sharing',
      'Interdiction and Disruption',
      'Screening, Search, and Detection'
    ]
  },
  protection: {
    name: 'Protection',
    color: 'purple',
    capabilities: [
      'Access Control and Identity Verification',
      'Cybersecurity',
      'Physical Protective Measures',
      'Risk Management for Protection Programs and Activities',
      'Supply Chain Integrity and Security'
    ]
  },
  mitigation: {
    name: 'Mitigation',
    color: 'green',
    capabilities: [
      'Community Resilience',
      'Long-term Vulnerability Reduction',
      'Risk and Disaster Resilience Assessment',
      'Threats and Hazards Identification'
    ]
  },
  response: {
    name: 'Response',
    color: 'orange',
    capabilities: [
      'Critical Transportation',
      'Environmental Response/Health and Safety',
      'Fatality Management Services',
      'Fire Management and Suppression',
      'Logistics and Supply Chain Management',
      'Mass Care Services',
      'Mass Search and Rescue Operations',
      'On-scene Security, Protection, and Law Enforcement',
      'Operational Communications',
      'Public Health, Healthcare, and Emergency Medical Services',
      'Situational Assessment'
    ]
  },
  recovery: {
    name: 'Recovery',
    color: 'teal',
    capabilities: [
      'Economic Recovery',
      'Health and Social Services',
      'Housing',
      'Infrastructure Systems',
      'Natural and Cultural Resources'
    ]
  }
};

// ============= HELPERS =============
export const getMissionAreaForCapability = (capabilityName) => {
  for (const [key, area] of Object.entries(MISSION_AREAS)) {
    if (area.capabilities.includes(capabilityName)) {
      return { key, ...area };
    }
  }
  return null;
};

export const getScoreColorClasses = (score) => {
  if (score >= 85) return { bg: 'bg-green-100', text: 'text-green-800', bar: 'bg-green-500', border: 'border-green-200', label: 'Strong' };
  if (score >= 75) return { bg: 'bg-yellow-100', text: 'text-yellow-800', bar: 'bg-yellow-500', border: 'border-yellow-200', label: 'Adequate' };
  if (score >= 60) return { bg: 'bg-orange-100', text: 'text-orange-800', bar: 'bg-orange-500', border: 'border-orange-200', label: 'Needs Improvement' };
  return { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-500', border: 'border-red-200', label: 'Critical Gap' };
};

export const groupCapabilitiesByMissionArea = (capabilitiesData) => {
  const grouped = { prevention: {}, protection: {}, mitigation: {}, response: {}, recovery: {} };
  Object.entries(capabilitiesData).forEach(([capName, data]) => {
    const missionArea = getMissionAreaForCapability(capName);
    if (missionArea && missionArea.key) {
      grouped[missionArea.key][capName] = data;
    }
  });
  return grouped;
};

// ============= LOADING SKELETONS =============
export function SkeletonCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="h-8 bg-gray-200 rounded w-1/2 mb-3"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    </div>
  );
}

export function SkeletonTableRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-1/3"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-1/3"></div></td>
      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
    </tr>
  );
}

export function SkeletonStatCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-1/4"></div>
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
      </div>
      <div className="lg:col-span-2"><SkeletonCard /></div>
      <div className="lg:col-span-1"><SkeletonCard /></div>
    </div>
  );
}

export function LoadingState() {
  return <DashboardSkeleton />;
}

// ============= PAGINATION =============
export function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange }) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;
    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
      <div className="text-sm text-gray-600">
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
      </div>
      <div className="flex items-center space-x-2">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition">
          Previous
        </button>
        <div className="hidden sm:flex items-center space-x-1">
          {getPageNumbers().map((page, index) => {
            if (page === '...') return <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">...</span>;
            return (
              <button key={page} onClick={() => onPageChange(page)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}>
                {page}
              </button>
            );
          })}
        </div>
        <div className="sm:hidden px-3 py-2 text-sm text-gray-700">Page {currentPage} of {totalPages}</div>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition">
          Next
        </button>
      </div>
    </div>
  );
}

// ============= NAV ITEM =============
export function NavItem({ icon, label, active, onClick, collapsed }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
        active ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-slate-700'
      }`}>
      {icon}
      {!collapsed && <span className="font-medium">{label}</span>}
    </button>
  );
}

// ============= WIDGET =============
export function Widget({ title, subtitle, actions, icon, children, className = '', action, onActionClick }) {
  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 flex-1">
          {icon && <div className="text-blue-600">{icon}</div>}
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex-shrink-0 ml-4">{actions}</div>}
        {action && onActionClick && (
          <button onClick={onActionClick} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            {action}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ============= STAT CARD =============
export function StatCard({ title, value, icon, color, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
    </div>
  );
}

// ============= STATUS BADGE =============
export function StatusBadge({ status }) {
  const statusConfig = {
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
    processing: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Processing' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
    archived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Archived' }
  };
  const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  return (
    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// ============= FORM FIELD =============
export function FormField({ label, required, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input type={type} required={required} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
    </div>
  );
}

// ============= FORM SELECT =============
export function FormSelect({ label, required, value, onChange, options, groups }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <select required={required} value={value} onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white">
          {groups ? (
            <>
              {options && options.filter(o => !o.group).map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
              {groups.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </optgroup>
              ))}
            </>
          ) : (
            options.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))
          )}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
}